import { navigate } from "gatsby";
import {
  JOURNEY_KEY,
  SCROLL_KEY_PREFIX,
  buildJourneyEntryState,
  buildJourneyRecord,
  decideJourneyExit,
  decideVerificaExit,
  parseJourneyRecord,
  parseScrollMemory,
  touchRecord,
  withChatOrigin,
} from "./chatJourney";

const NAV_KEY = "chatmutuo:nav";
const TTL_MS = 30 * 60 * 1000;

export function saveScrollAndNavigateToChat(
  url,
  { treatAsReturnTarget = false, widgetWasOpen = false } = {}
) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      NAV_KEY,
      JSON.stringify({
        fromPath: window.location.pathname,
        scrollY: window.scrollY || window.pageYOffset || 0,
        // Sempre booleani: un oggetto truthy finirebbe serializzato per intero.
        treatAsReturnTarget: !!treatAsReturnTarget,
        widgetWasOpen: !!widgetWasOpen,
        ts: Date.now(),
      })
    );
  } catch (_) {}
  navigate(url);
}

export function saveScrollForWidgetMutuoNav(url, navigateOpts = {}) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      NAV_KEY,
      JSON.stringify({
        fromPath: window.location.pathname,
        scrollY: window.scrollY || window.pageYOffset || 0,
        treatAsReturnTarget: true,
        widgetWasOpen: true,
        ts: Date.now(),
      })
    );
  } catch (_) {}
  navigate(url, {
    ...navigateOpts,
    state: { ...(navigateOpts.state || {}), from: window.location.pathname, full: true },
  });
}

export function readChatNavPayload() {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(NAV_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p.ts || Date.now() - p.ts > TTL_MS) {
      sessionStorage.removeItem(NAV_KEY);
      return null;
    }
    return p;
  } catch (_) {
    return null;
  }
}

export function clearChatNavPayload() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(NAV_KEY);
  } catch (_) {}
}

export function saveChatNavForMobileReturn(fromPath) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      NAV_KEY,
      JSON.stringify({
        fromPath,
        scrollY: 0,
        treatAsReturnTarget: false,
        widgetWasOpen: true,
        ts: Date.now(),
      })
    );
  } catch (_) {}
}

export function handleServiceBarChatClick(
  chatSlug,
  currentPathname,
  { treatAsReturnTarget = false } = {}
) {
  const target = `/${chatSlug}`;
  const isOnChat =
    currentPathname === target || currentPathname === `${target}/`;

  if (!isOnChat) {
    saveScrollAndNavigateToChat(target, { treatAsReturnTarget });
    return;
  }

  const payload = readChatNavPayload();
  if (payload?.treatAsReturnTarget) {
    navigate(-1);
  } else {
    clearChatNavPayload();
    navigate("/");
  }
}

// ---------------------------------------------------------------------------
// Percorso chat (ChatMutuo → Scheda/Verifica → ritorno). Le decisioni sono in
// ./chatJourney (funzioni pure, testate); qui solo sessionStorage e navigate.
// ---------------------------------------------------------------------------

export function startChatJourney({ surface, anchorHref, anchorIndex = 0 }) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      JOURNEY_KEY,
      JSON.stringify(
        buildJourneyRecord({
          surface,
          chatPath: window.location.pathname,
          anchorHref,
          anchorIndex,
          now: Date.now(),
        })
      )
    );
  } catch (_) {}
}

export function readChatJourney() {
  if (typeof window === "undefined") return null;
  try {
    return parseJourneyRecord(sessionStorage.getItem(JOURNEY_KEY), Date.now());
  } catch (_) {
    return null;
  }
}

// Consuma solo l'ancora: il percorso resta valido per Verifica → Scheda → chat.
export function clearJourneyAnchor() {
  const journey = readChatJourney();
  if (!journey) return;
  try {
    const { anchorHref, anchorIndex, ...rest } = journey;
    sessionStorage.setItem(JOURNEY_KEY, JSON.stringify(rest));
  } catch (_) {}
}

// Unico ingresso dalla chat verso Scheda/Verifica (link con ?ChatBotAction=).
// Il chiamante fa dispatch(CHAT_MUTUO_HIDE()) prima, se surface === "widget".
// anchorHref: valore stabile per ritrovare il bottone al rientro (i bottoni
// CMS navigano con un &ts= che cambia a ogni click).
export function openFromChat(href, { surface, anchorHref = href, anchorIndex = 0 }) {
  if (typeof window === "undefined") return;
  const chatPath = window.location.pathname;
  startChatJourney({ surface, anchorHref, anchorIndex });
  if (surface === "widget") {
    // Payload letto da shouldUpdateScroll per riaprire il widget e
    // ripristinare lo scroll della pagina sottostante.
    try {
      sessionStorage.setItem(
        NAV_KEY,
        JSON.stringify({
          fromPath: chatPath,
          scrollY: window.scrollY || window.pageYOffset || 0,
          treatAsReturnTarget: true,
          widgetWasOpen: true,
          ts: Date.now(),
        })
      );
    } catch (_) {}
  }
  navigate(withChatOrigin(href), { state: buildJourneyEntryState(chatPath) });
}

// Ogni passo del percorso (Scheda, Verifica) rinnova la TTL dell'ancora e del
// payload che riapre il widget desktop.
export function touchChatJourney() {
  if (typeof window === "undefined") return;
  try {
    const now = Date.now();
    [JOURNEY_KEY, NAV_KEY].forEach((key) => {
      const touched = touchRecord(sessionStorage.getItem(key), now);
      if (touched) sessionStorage.setItem(key, touched);
    });
  } catch (_) {}
}

// X / matita su Scheda e Verifica. Restituisce false fuori da un percorso
// chat: il chiamante prosegue con la sua logica di sempre.
export function exitChatJourneyStep(location) {
  if (typeof window === "undefined") return false;
  const exit = decideJourneyExit({
    state: location?.state,
    historyLength: window.history.length,
  });
  if (!exit) return false;
  if (exit.type === "back") {
    navigate(-1);
  } else {
    navigate(exit.to, { replace: exit.replace });
  }
  return true;
}

// X / matita della Verifica fuori dal percorso chat (decisione in chatJourney).
export function exitVerificaStep({ from, step }) {
  const exit = decideVerificaExit({ from, step });
  if (exit.type === "back") {
    navigate(-1);
  } else {
    navigate(exit.to);
  }
}

export function readChatScrollMemory(surface) {
  if (typeof window === "undefined") return null;
  try {
    return parseScrollMemory(sessionStorage.getItem(SCROLL_KEY_PREFIX + surface));
  } catch (_) {
    return null;
  }
}

export function writeChatScrollMemory(surface, { top, atBottom }) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      SCROLL_KEY_PREFIX + surface,
      JSON.stringify({ top, atBottom })
    );
  } catch (_) {}
}
